import { getBlock, getBlockContext } from '../blocks/index';
import { getBootstrapScriptUrl, getBootstrapStylesUrl } from '../blocks/utils';
import type {
    TBlock,
    TMappedStaticBlocks,
    TMappedStaticBlocksConverted,
    TMFEName
} from '../blocks/types';
import { browser } from '$app/environment';
import type { TMarket } from '../market/types';
import type { TDeviceDetails } from '../device/types';
import { getReferences } from '../page/utils';
import { getMFELocationOverrides } from '../devTools';
import { GATEKEEPER_VERSION } from '../gatekeeper/manage-gatekeeper.svelte';
import { Gatekeeper, type TEnvContext } from '../gatekeeper/gatekeeper';
import type { Cookies } from '@sveltejs/kit';
import {
    CACHE_FRESHNESS_HEADER,
    CACHE_KEY_HEADER,
    CACHE_TAGS_HEADER,
    CACHE_TTL_HEADER,
    type TUrlPrefix
} from '@casumo/fabric-fundamentals';

export type TStaticBlocks = {
    blockData: TBlock[];
    cssReferences: string[];
    scriptReferences: string[];
};

export type TMFEBlock = {
    moduleName: string;
    mfe: TMFEName;
    props?: Record<string, string | undefined>;
};

export type TBuildStaticBlocksParams = {
    market: TMarket;
    deviceDetails: TDeviceDetails;
    envContext: TEnvContext;
    ssrGatekeeper?: Gatekeeper;
    cookies?: Cookies;
};

const EMPTY_BLOCK_DATA = {
    blockData: [],
    cssReferences: [],
    scriptReferences: []
};

// This can run both on the server and client side
export const buildStaticBlocks = async (
    blocks: TMFEBlock[],
    { market, deviceDetails, envContext, ssrGatekeeper, cookies }: TBuildStaticBlocksParams
): Promise<TStaticBlocks> => {
    if (!browser && !ssrGatekeeper) {
        console.error('SSR Gatekeeper is required for static blocks rendering on the server.');
        return EMPTY_BLOCK_DATA;
    }

    if (blocks.length === 0) {
        return EMPTY_BLOCK_DATA;
    }

    try {
        const mfeLocationOverrides = getMFELocationOverrides(cookies);
        const gatekeeper = browser
            ? new Gatekeeper(
                  window.fabric.getGatekeeper(GATEKEEPER_VERSION as 'v2.2.14', 'fabric-spine-v2'),
                  envContext
              )
            : ssrGatekeeper!;

        const blockContext = await getBlockContext(
            market,
            deviceDetails,
            envContext.route,
            new URL(envContext.url),
            envContext.countryCodeByIp,
            envContext.operationalNavigationSlugs,
            gatekeeper
        );

        const blockData = blocks.map(({ mfe, moduleName, props }) =>
            getBlock(
                { moduleName, props: { component: moduleName, ...props }, mfe, anchor: '' },
                blockContext,
                '',
                [getBootstrapStylesUrl(mfe, mfeLocationOverrides)],
                getBootstrapScriptUrl(mfe, mfeLocationOverrides),
                true,
                false
            )
        );

        const { cssReferences, scriptReferences } = getReferences(blockData);

        return {
            blockData,
            cssReferences,
            scriptReferences
        };
    } catch (error) {
        console.error(error);
        return EMPTY_BLOCK_DATA;
    }
};

// Only in the browser
export const fetchAndBuildStaticBlocks = async (
    marketSlug: TUrlPrefix,
    fetch: typeof globalThis.fetch,
    staticBlocksParams: TBuildStaticBlocksParams
): Promise<TMappedStaticBlocksConverted> => {
    const emptyResponse = {
        blocks: {
            commonBlocks: EMPTY_BLOCK_DATA,
            losOnlyBlocks: EMPTY_BLOCK_DATA,
            lisOnlyBlocks: EMPTY_BLOCK_DATA
        },
        excludedPageGroups: []
    };

    if (!browser) {
        console.error('fetchAndBuildStaticBlocks can only be called in the browser.');
        return emptyResponse;
    }

    const endpoint = `/spine-api/static-blocks/${marketSlug}`;
    const headers = new Headers({
        [CACHE_KEY_HEADER]: `${marketSlug}_static-blocks`,
        [CACHE_TTL_HEADER]: (60 * 60 * 24).toString(), // 1d
        [CACHE_FRESHNESS_HEADER]: (60 * 60 * 2).toString(), // 2h
        [CACHE_TAGS_HEADER]: `static-blocks,${marketSlug}`
    });

    try {
        const response = await fetch(endpoint, { headers });
        const responseJson = (await response.json()) as TMappedStaticBlocks;

        const [commonBlocks, losOnlyBlocks, lisOnlyBlocks] = await Promise.all([
            buildStaticBlocks(responseJson?.blocks?.commonBlocks ?? [], staticBlocksParams),
            buildStaticBlocks(responseJson?.blocks?.losOnlyBlocks ?? [], staticBlocksParams),
            buildStaticBlocks(responseJson?.blocks?.lisOnlyBlocks ?? [], staticBlocksParams)
        ]);

        return {
            excludedPageGroups: responseJson.excludedPageGroups,
            blocks: {
                commonBlocks,
                losOnlyBlocks,
                lisOnlyBlocks
            }
        };
    } catch (e) {
        console.error('Error during fetchAndBuildStaticBlocks:', e);
        return emptyResponse;
    }
};
